import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CampaignModalChip } from '#/entities/campaign'
import { useCampaignSourceOptions } from '#/features/campaign-enrol'
import type { CampaignFilterField } from '#/shared/api/generated/communication/models'
import { useWheelScrollProxy } from '#/shared/lib/hooks'
import { cn } from '#/shared/lib/utils'
import { Button } from '#/shared/ui/button'
import { Input } from '#/shared/ui/input'
import { Popover } from '#/shared/ui/popover'

import {
  CAMPAIGN_FILTER_FIELD_TYPES,
  filterValueLabel,
} from '../model/use-campaign-join-filters'
import type {
  CampaignFilterValue,
  UseCampaignJoinFiltersResult,
} from '../model/use-campaign-join-filters'

/** Day counts offered before the free-typed one. */
const DAY_PRESETS = [3, 7, 14, 30, 60, 90] as const

const ROW_CLASS =
  'flex w-full cursor-pointer items-center rounded-md px-2.5 py-1.5 text-left text-xs text-gray-900 hover:bg-gray-50'

function OptionRow({
  label,
  selected,
  onClick,
}: {
  label: string
  selected?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        ROW_CLASS,
        selected === true && 'bg-avatar-blue-soft text-campaign-running',
      )}
    >
      {label}
    </button>
  )
}

function DaysPicker({ onApply }: { onApply: (days: number) => void }) {
  const { t } = useTranslation()
  const [custom, setCustom] = useState('')
  const parsed = Number.parseInt(custom, 10)
  const valid = Number.isFinite(parsed) && parsed > 0
  return (
    <div className="flex flex-col gap-1">
      {DAY_PRESETS.map((days) => (
        <OptionRow
          key={days}
          label={t('campaigns.modal.join.filter.days', { count: days })}
          onClick={() => onApply(days)}
        />
      ))}
      <div className="flex items-center gap-1.5 px-1 pt-1">
        <Input
          inputSize="sm"
          type="number"
          min={1}
          value={custom}
          onChange={(event) => setCustom(event.target.value)}
          placeholder={t('campaigns.modal.join.filter.customDays')}
          className="input-mobile-safe"
        />
        <Button
          size="xs"
          disabled={!valid}
          onClick={() => valid && onApply(parsed)}
        >
          {t('campaigns.modal.join.filter.apply')}
        </Button>
      </div>
    </div>
  )
}

interface PickOption {
  value: string
  label: string
}

type ApplyValue = (value: CampaignFilterValue, shown?: string) => void

function MultiEnumPicker({
  options,
  loading = false,
  onApply,
}: {
  options: PickOption[]
  loading?: boolean
  onApply: ApplyValue
}) {
  const { t } = useTranslation()
  const [picked, setPicked] = useState<string[]>([])
  const toggle = (value: string) =>
    setPicked((current) =>
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value],
    )
  const scrollRef = useRef<HTMLDivElement>(null)
  // The popover is portaled out of the dialog, whose scroll lock would
  // otherwise swallow the wheel over this list.
  useWheelScrollProxy(scrollRef)
  const apply = () =>
    onApply(
      picked,
      options
        .filter((option) => picked.includes(option.value))
        .map((option) => option.label)
        .join(', '),
    )
  return (
    <div className="flex flex-col gap-1">
      <div
        ref={scrollRef}
        className="flex max-h-56 flex-col gap-1 overflow-y-auto"
      >
        {options.map((option) => (
          <OptionRow
            key={option.value}
            label={option.label}
            selected={picked.includes(option.value)}
            onClick={() => toggle(option.value)}
          />
        ))}
        {!loading && options.length === 0 && (
          <p className="px-2.5 py-1.5 text-xs text-gray-300">
            {t('campaigns.modal.join.filter.noOptions')}
          </p>
        )}
      </div>
      <div className="px-1 pt-1">
        <Button
          size="xs"
          className="w-full"
          disabled={picked.length === 0}
          onClick={apply}
        >
          {t('campaigns.modal.join.filter.apply')}
        </Button>
      </div>
    </div>
  )
}

/**
 * A multi-choice field the service publishes without options: its values are
 * the org's sources, which live in the shipment service's curated list rather
 * than in a fixed enum. Fetched only while this picker is on screen.
 */
function SourcePicker({ onApply }: { onApply: ApplyValue }) {
  const sources = useCampaignSourceOptions(true)
  return (
    <MultiEnumPicker
      options={sources.options}
      loading={sources.isLoading}
      onApply={onApply}
    />
  )
}

/** The second step of the popover: a value for the field just picked. */
function ValuePicker({
  field,
  onApply,
}: {
  field: CampaignFilterField
  onApply: ApplyValue
}) {
  const { t } = useTranslation()
  const options = field.options ?? []
  switch (field.type) {
    case CAMPAIGN_FILTER_FIELD_TYPES.days:
      return <DaysPicker onApply={onApply} />
    case CAMPAIGN_FILTER_FIELD_TYPES.multiEnum:
      return options.length === 0 ? (
        <SourcePicker onApply={onApply} />
      ) : (
        // The rows are labelled here rather than in the chip alone, because
        // what the picker shows is also what it hands back as the chip's
        // `shown` text — label them once and both read the same.
        <MultiEnumPicker
          options={options.map((value) => ({
            value,
            label: filterValueLabel(value, t),
          }))}
          onApply={onApply}
        />
      )
    default:
      return (
        <div className="flex flex-col gap-1">
          {options.map((option) => (
            <OptionRow
              key={option}
              label={filterValueLabel(option, t)}
              onClick={() => onApply(option)}
            />
          ))}
        </div>
      )
  }
}

interface CampaignFilterBuilderProps {
  filters: UseCampaignJoinFiltersResult
}

/**
 * The chip row on "everyone who matches a filter". Each set filter is a chip
 * (click removes it); "+ Add filter" opens a two-step popover — pick a field
 * the service offers, then a value it accepts. Portaled: the dialog body
 * scrolls, and an in-flow panel would be clipped by it.
 */
export function CampaignFilterBuilder({ filters }: CampaignFilterBuilderProps) {
  const { t } = useTranslation()
  const [field, setField] = useState<CampaignFilterField | null>(null)
  const [open, setOpen] = useState(false)

  const onOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) setField(null)
  }
  const apply: ApplyValue = (value, shown) => {
    if (field != null) filters.setFilter(field.key, value, shown)
    onOpenChange(false)
  }

  return (
    <>
      {filters.chips.map((chip) => (
        <CampaignModalChip
          key={chip.key}
          label={chip.label}
          selected
          onClick={() => filters.removeFilter(chip.key)}
        />
      ))}
      <Popover
        portal
        open={open}
        onOpenChange={onOpenChange}
        matchTriggerWidth={false}
        // The Popover panel is bare; the surface is the consumer's, same as the
        // date picker's.
        contentClassName="w-56 rounded-md border border-gray-200 bg-white p-1.5 shadow-popover"
        // The popover's own wrapper toggles on click; a second toggle on the
        // chip would cancel it out.
        trigger={() => (
          <CampaignModalChip
            label={t('campaigns.modal.join.filter.add')}
            selected={false}
          />
        )}
      >
        {field == null ? (
          <div className="flex flex-col gap-1">
            <p className="px-2.5 pt-1 pb-0.5 text-2xs font-medium tracking-wider text-warm-500">
              {t('campaigns.modal.join.filter.pickField')}
            </p>
            {filters.available.map((item) => (
              <OptionRow
                key={item.key}
                label={item.label}
                onClick={() => setField(item)}
              />
            ))}
            {!filters.isLoading && filters.available.length === 0 && (
              <p className="px-2.5 py-1.5 text-xs text-gray-300">
                {t('campaigns.modal.join.filter.none')}
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            <p className="px-2.5 pt-1 pb-0.5 text-2xs font-medium tracking-wider text-warm-500">
              {field.label}
            </p>
            <ValuePicker field={field} onApply={apply} />
          </div>
        )}
      </Popover>
    </>
  )
}
